import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AUDIO_REGEXP, IMAGE_REGEXP, VIDEO_REGEXP } from '../const/media.const';
import { EMediaProvider, EMediaType } from '../enums/media-type.enum';
import { FileType } from '@kiwid-app/kiwid-ui-lib-angular/k-stl-model-viewer';

export class FileHelper {
    static getMediaTypeByName(name: string): EMediaType {
        if (!name) {
            return;
        }

        const nameLowercase = name.toLocaleLowerCase();

        if (nameLowercase.match(IMAGE_REGEXP)) {
            return EMediaType.IMAGE;
        }

        if (nameLowercase.match(VIDEO_REGEXP)) {
            return EMediaType.VIDEO;
        }

        if (nameLowercase.match(AUDIO_REGEXP)) {
            return EMediaType.AUDIO;
        }

        return EMediaType.RAW;
    }

    static getProviderByName(name: string): EMediaProvider {
        if (!name) {
            return;
        }

        const nameLowercase = name.toLocaleLowerCase();
        return EMediaProvider.AWS;

        // if (nameLowercase.match(IMAGE_REGEXP)) {
        //     return EMediaProvider.CLOUDINARY;
        // }
        //
        // if (nameLowercase.match(VIDEO_REGEXP)) {
        //     return EMediaProvider.CLOUDINARY;
        // }
        //
        // if (nameLowercase.match(AUDIO_REGEXP)) {
        //     return EMediaProvider.CLOUDINARY;
        // }
        //
        // return EMediaProvider.AWS;
    }

    static isRaw(name: string): boolean {
        return FileHelper.getMediaTypeByName(name) === EMediaType.RAW;
    }

    static isHtml3DModelOrUrl(name: string): boolean {
        return name.toLowerCase().indexOf('.html') > -1 || name.toLowerCase().indexOf('.stl') > -1 || name.toLowerCase().indexOf('.ply') > -1 || name.toLowerCase().indexOf('.pdf') > -1;
    }

    static isHtmlOrPdf(name: string): boolean {
        return name.toLowerCase().indexOf('.html') > -1 || name.toLowerCase().indexOf('.pdf') > -1;
    }

    static is3DModel(name: string): boolean {
        return name.toLowerCase().indexOf('.stl') > -1 || name.toLowerCase().indexOf('.ply') > -1;
    }

    static get3DModelType(name: string): FileType | null {
        if (name.toLowerCase().indexOf('.stl') > -1) {
            return FileType.STL;
        }

        if (name.toLowerCase().indexOf('.ply') > -1) {
            return FileType.PLY;
        }

        return null;
    }

    static isPDF(name: string): boolean {
        return name.toLowerCase().indexOf('.pdf') > -1;
    }

    static isVideo(name: string): boolean {
        return FileHelper.getMediaTypeByName(name) === EMediaType.VIDEO;
    }

    static isAudio(name: string): boolean {
        return FileHelper.getMediaTypeByName(name) === EMediaType.AUDIO;
    }

    static isImage(name: string): boolean {
        return FileHelper.getMediaTypeByName(name) === EMediaType.IMAGE;
    }

    static isHeic(name: string): boolean {
        const extension = name.split('.').pop();
        return ['heic', 'heif'].includes(extension);
        // return name.toLowerCase().indexOf('.heic') > -1;
    }

    static isImageOrVideo(name: string): boolean {
        return [EMediaType.IMAGE, EMediaType.VIDEO].includes(FileHelper.getMediaTypeByName(name));
    }

    static async openFileInWindow(url: string, target: string = null): Promise<void> {
        const link = document.createElement('a');
        link.href = url;

        if (target) {
            link.target = target;
        }

        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    static async forceDownloadWithUrl(url: string, target: string = null, download: boolean = false, fileName?: string): Promise<void> {
        const name = url.split('/').pop();
        const extension = name.split('.').pop();

        const link = document.createElement('a');

        if (['html'].includes(extension) && !download) {
            const blob = await fetch(url).then((r) => r.blob());
            const fileContents = await new Response(blob).text();
            link.href = window.URL.createObjectURL(new Blob([fileContents], { type: 'text/html' }));
        } else if (['pdf'].includes(extension)) {
            const blob = await fetch(url).then((r) => r.blob());
            link.href = window.URL.createObjectURL(blob);
        } else {
            link.href = url;
        }

        if (target) {
            link.target = target;
        }

        if (!['pdf'].includes(extension) || download) {
            link.setAttribute('download', fileName ?? name);
        }

        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    static async forceDownloadWithUrlAndName(url: string, target: string = null, fileName?: string): Promise<void> {
        const name = url.split('/').pop();
        const link = document.createElement('a');
        const blob = await fetch(url).then((r) => r.blob());
        // const fileContents = await new Response(blob).text();
        // link.href = window.URL.createObjectURL(new Blob([fileContents], { type: blob.type }));
        link.href = URL.createObjectURL(blob);

        if (target) {
            link.target = target;
        }

        link.setAttribute('download', fileName ?? name);
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    static embedGoogleURL(domSanitizer: DomSanitizer, url: string): SafeUrl {
        return domSanitizer.bypassSecurityTrustResourceUrl(`https://docs.google.com/gview?embedded=true&url=${url}`);
    }

    private static readonly sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    static formatSize(sizeValue: number, type: 'kb' | 'mb' | 'b' = 'kb'): string {
        const multipliers = { b: 1, kb: 1024, mb: 1024 * 1024 };
        let size = sizeValue * multipliers[type];
        let index = 0;

        while (size >= 1024 && index < this.sizes.length - 1) {
            size /= 1024;
            index++;
        }

        const formattedSize = parseFloat(size.toFixed(2)).toString();

        return `${formattedSize} ${this.sizes[index]}`;
    }

    static getExtension(filename: string): string {
        const parts = filename.split('.');
        return parts.length > 1 ? `.${parts[parts.length - 1].toLowerCase()}` : '';
    };
}
